"use client";

import { Tooltip } from "@lattice/frontend/components/ui/tooltip";
import { cn } from "@lattice/frontend/lib/utils/ui";
import {
  financial_ratios_groups,
  key_metrics_groups,
} from "./stock-statistics-section";
import { trpcApi } from "@lattice/frontend/lib/trpc/client/index";
import { cardPadding, cardGridGap } from "@lattice/frontend/lib/styles/common";

// Define which metrics should be displayed as percentages
const percentageMetrics = new Set([
  "grossProfitMarginTTM",
  "ebitdaMarginTTM",
  "operatingProfitMarginTTM",
  "netProfitMarginTTM",
  "dividendYieldTTM",
  "returnOnAssetsTTM",
  "returnOnEquityTTM",
  "returnOnInvestedCapitalTTM",
  "earningsYieldTTM",
  "freeCashFlowYieldTTM",
]);

export default function StockStatisticsSectionContent({
  symbol,
}: {
  symbol: string;
}) {
  const [data] = trpcApi.stock.get_statistics.useSuspenseQuery({
    symbol,
  });

  return (
    <>
      {data.key_metrics && data.ratios ? (
        <div className={cn("grid grid-cols-1 lg:grid-cols-2", cardGridGap)}>
          <div>
            {key_metrics_groups.map((group) => (
              <div
                key={group.name}
                className={cn(
                  "mb-4 bg-grey-7 rounded-lg border border-[#131314]",
                  cardPadding,
                )}
              >
                <h4 className="font-sans text-xs font-normal text-muted-foreground/80 uppercase tracking-wider mb-4 sm:mb-6">
                  {group.name}
                </h4>
                <ul className="space-y-0">
                  {group.stats.map((stat, index) => (
                    <li
                      key={stat.key}
                      className={`py-2.5 flex justify-between ${index < group.stats.length - 1 ? "border-b border-[#131314]" : ""}`}
                    >
                      <span className="text-sm sm:text-base">{stat.name}</span>
                      <Tooltip content={data.key_metrics?.[stat.key]}>
                        <span className="cursor-pointer text-sm sm:text-base">
                          {data.key_metrics?.[stat.key] === null
                            ? "--"
                            : percentageMetrics.has(stat.key)
                              ? `${(Number(data.key_metrics?.[stat.key]) * 100).toFixed(1)}%`
                              : !data.key_metrics?.[stat.key]
                                    ?.toString()
                                    .includes(".") ||
                                  Number(data.key_metrics?.[stat.key]) >=
                                    10000 ||
                                  Number(data.key_metrics?.[stat.key]) <= 10000
                                ? new Intl.NumberFormat("en-US", {
                                    notation: "compact",
                                    compactDisplay: "short",
                                  }).format(
                                    Number(data.key_metrics?.[stat.key]),
                                  )
                                : Number(data.key_metrics?.[stat.key]).toFixed(
                                    2,
                                  )}
                        </span>
                      </Tooltip>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div>
            {financial_ratios_groups.map((group) => {
              // Skip Dividend Metrics section if dividendYieldTTM is null or not present
              if (
                group.name === "Dividend Metrics" &&
                !data.ratios?.dividendYieldTTM
              ) {
                return null;
              }

              return (
                <div
                  key={group.name}
                  className={cn(
                    "mb-4 bg-grey-7 rounded-lg border border-[#131314]",
                    cardPadding,
                  )}
                >
                  <h4 className="font-sans text-xs font-normal text-muted-foreground/80 uppercase tracking-wider mb-4 sm:mb-6">
                    {group.name}
                  </h4>
                  <ul className="space-y-0">
                    {group.stats.map((stat, index) => (
                      <li
                        key={stat.key}
                        className={`py-2.5 flex justify-between ${index < group.stats.length - 1 ? "border-b border-[#131314]" : ""}`}
                      >
                        <span className="text-sm sm:text-base">
                          {stat.name}
                        </span>
                        <Tooltip content={data.ratios?.[stat.key]}>
                          <span className="cursor-pointer text-sm sm:text-base">
                            {data.ratios?.[stat.key] === null
                              ? "--"
                              : percentageMetrics.has(stat.key)
                                ? `${(Number(data.ratios?.[stat.key]) * 100).toFixed(1)}%`
                                : data.ratios?.[stat.key]
                                      .toString()
                                      .includes(".")
                                  ? Number(data.ratios?.[stat.key]).toFixed(2)
                                  : new Intl.NumberFormat("en-US", {
                                      notation: "compact",
                                      compactDisplay: "short",
                                    }).format(Number(data.ratios?.[stat.key]))}
                          </span>
                        </Tooltip>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <p className="text-muted-foreground">No statistics available</p>
      )}
    </>
  );
}
